import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, ArrowRight, Camera, Sparkles, AlertTriangle } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts';
import ReceiptSnap from '../client-pwa/ReceiptSnap';

const healthHistory = [
  { m: 'Nov', score: 64 }, { m: 'Dec', score: 68 }, { m: 'Jan', score: 71 },
  { m: 'Feb', score: 74 }, { m: 'Mar', score: 78 }, { m: 'Apr', score: 82 },
];

const tickers = [
  { label: 'Revenue YTD', value: '$284,500', delta: '+12.4%', positive: true },
  { label: 'Net profit', value: '$48,200', delta: '+8.1%', positive: true },
  { label: 'Cash position', value: '$35,676', delta: '-3.2%', positive: false },
  { label: 'Debtors', value: '$18,420', delta: '+22%', positive: false },
];

export default function HomePageV3() {
  const [receiptOpen, setReceiptOpen] = useState(false);

  return (
    <div className="space-y-8 pt-6">
      {/* SECTION: Lead — editorial brief */}
      <article>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ 01</span>
          <p className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">Lead Article</p>
        </div>
        <h2 className="mt-2 font-serif text-2xl font-bold leading-tight tracking-tight text-stone-900 dark:text-stone-100">
          A strong week — revenue up <span className="text-teal-700 dark:text-teal-400">14%</span>, three invoices paid promptly
        </h2>
        <p className="mt-3 border-l-2 border-stone-300 dark:border-stone-700 pl-3 font-serif text-[15px] italic leading-relaxed text-stone-600 dark:text-stone-400">
          Last week brought $32,400 in receipts, your highest weekly tally this quarter. Two outstanding amounts warrant attention this week, including Acme Builders ($4,200, 12 days overdue).
        </p>
      </article>

      {/* Ticker grid */}
      <section className="border-y border-stone-300 dark:border-stone-700">
        <div className="grid grid-cols-2 divide-x divide-stone-200 dark:divide-stone-800">
          {tickers.slice(0, 2).map(t => (
            <div key={t.label} className="px-3 py-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{t.label}</p>
              <p className="mt-1 font-serif text-2xl font-bold tabular-nums tracking-tight text-stone-900 dark:text-stone-100">{t.value}</p>
              <p className={`mt-0.5 inline-flex items-center gap-0.5 text-[12px] font-bold tabular-nums ${t.positive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                {t.positive ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
                {t.delta}
              </p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 divide-x divide-stone-200 dark:divide-stone-800 border-t border-stone-200 dark:border-stone-800">
          {tickers.slice(2).map(t => (
            <div key={t.label} className="px-3 py-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{t.label}</p>
              <p className="mt-1 font-serif text-2xl font-bold tabular-nums tracking-tight text-stone-900 dark:text-stone-100">{t.value}</p>
              <p className={`mt-0.5 inline-flex items-center gap-0.5 text-[12px] font-bold tabular-nums ${t.positive ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                {t.positive ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
                {t.delta}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION: Health index — sparkline */}
      <section>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ 02</span>
          <p className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">Health Index</p>
        </div>
        <div className="mt-3 border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-serif text-5xl font-bold tabular-nums leading-none tracking-tight text-stone-900 dark:text-stone-100">82</p>
              <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">Composite score · 0–100</p>
            </div>
            <div className="text-right">
              <p className="inline-flex items-center gap-1 border border-emerald-700/30 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[12px] font-bold text-emerald-800 dark:text-emerald-300">
                <ArrowUpRight className="h-3 w-3" /> +4 PTS
              </p>
              <p className="mt-1 text-[12px] text-stone-500">six-month change</p>
            </div>
          </div>

          <div className="-mx-2 mt-4 h-14">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={healthHistory}>
                <YAxis hide domain={[40, 100]} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#0f766e"
                  strokeWidth={1.75}
                  dot={{ r: 2.5, fill: '#0f766e' }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="-mt-1 flex justify-between px-3 font-mono text-[11px] text-stone-400 dark:text-stone-500">
            {healthHistory.map(p => <span key={p.m}>{p.m}</span>)}
          </div>
        </div>
      </section>

      {/* SECTION: Brief — AI weekly */}
      <section>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ 03</span>
          <p className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">From the Editor's Desk</p>
        </div>
        <div className="mt-3 border-l-4 border-teal-700 dark:border-teal-500 bg-white dark:bg-stone-900 px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3 w-3 text-teal-700 dark:text-teal-400" />
            <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-400">AI Briefing · Generated 06:00</p>
          </div>
          <p className="mt-2 font-serif text-base leading-relaxed text-stone-800 dark:text-stone-200">
            Three customers paid promptly. Equipment GST credits will reduce your Q3 BAS liability by an estimated <span className="font-bold">$1,180</span>. Worth chasing this week:
          </p>
          <div className="mt-3 flex items-start gap-2 border border-amber-300/50 bg-amber-50 dark:bg-amber-950/40 p-3">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
            <p className="text-[14px] leading-relaxed text-stone-800 dark:text-stone-200">
              <span className="font-bold">Acme Builders</span> · $4,200 outstanding 12 days. Recommended action: send formal reminder.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION: Quick action */}
      <section>
        <button
          onClick={() => setReceiptOpen(true)}
          className="group flex w-full items-center gap-4 border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-5 py-4 text-left transition-colors hover:border-teal-700"
        >
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-teal-700 dark:border-teal-500 bg-teal-700 dark:bg-teal-500 text-white">
            <Camera className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-400">Quick capture</p>
            <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">Photograph a receipt</p>
            <p className="text-xs text-stone-500">AI extracts vendor, amount and GST in 1.4s</p>
          </div>
          <ArrowRight className="h-4 w-4 text-stone-400 dark:text-stone-500 transition-transform group-hover:translate-x-1" />
        </button>
      </section>

      {/* SECTION: Calendar */}
      <section>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ 04</span>
          <p className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">On the Calendar</p>
        </div>
        <div className="mt-3 divide-y divide-stone-200 dark:divide-stone-800 border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          {[
            { date: 'Apr 28', daysOut: 12, title: 'Q3 BAS lodgement', tag: 'Tax', urgency: 'amber' },
            { date: 'May 15', daysOut: 29, title: 'Superannuation deadline', tag: 'Super', urgency: 'normal' },
            { date: 'Jun 30', daysOut: 75, title: 'EOFY tax planning window closes', tag: 'Strategy', urgency: 'normal' },
          ].map((it, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3.5">
              <div className="w-12 flex-shrink-0">
                <p className="font-mono text-[12px] font-bold uppercase text-stone-500">{it.date}</p>
                <p className="font-mono text-[11px] text-stone-400 dark:text-stone-500">+{it.daysOut}d</p>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-stone-900 dark:text-stone-100">{it.title}</p>
                <p className="text-[12px] uppercase tracking-wider text-stone-500">{it.tag}</p>
              </div>
              {it.urgency === 'amber' && (
                <span className="border border-amber-700/30 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase text-amber-800 dark:text-amber-300">
                  Soon
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* SECTION: Correspondence */}
      <section>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[12px] font-bold text-teal-700 dark:text-teal-400">§ 05</span>
          <p className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">Correspondence</p>
        </div>
        <Link to="/client-pwa-v3/help" className="mt-3 block border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4 transition-colors hover:border-stone-400">
          <div className="flex items-baseline justify-between">
            <p className="text-[12px] font-bold uppercase tracking-[0.15em] text-stone-500">From Marcus Bell</p>
            <p className="font-mono text-[11px] text-stone-400 dark:text-stone-500">2h ago</p>
          </div>
          <p className="mt-2 font-serif text-sm leading-relaxed italic text-stone-700 dark:text-stone-300">
            "I've reviewed your March P&amp;L — looks good. One quick question about the new equipment purchase…"
          </p>
          <div className="mt-2 flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-400">
            Read in full <ArrowRight className="h-3 w-3" />
          </div>
        </Link>
      </section>

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />
    </div>
  );
}
